<script setup lang="ts">
import type { LogoItem } from '~/constants/brands'
import { getWaterBrandSlugByCode, WATER_CATEGORY_SLUG } from '~/utils/waterBrandLogos'

const props = withDefaults(defineProps<{
  items: LogoItem[]
  compact?: boolean
}>(), {
  compact: false,
})

const localePath = useLocalePath()

function onLogoClick(item: LogoItem) {
  if (item.slug) {
    navigateTo(localePath(`/brand/${item.slug}`))
    return
  }
  if (!item.code) return
  const slug = getWaterBrandSlugByCode(item.code)
  if (!slug) return
  navigateTo(localePath(`/category/${WATER_CATEGORY_SLUG}/${slug}`))
}

const carouselRef = ref(null)
const swiper = useSwiper(carouselRef, {
  loop: true,
  grabCursor: true,
  autoplay: {
    delay: 0,
    disableOnInteraction: false,
    pauseOnMouseEnter: true,
  },
  speed: 1000,
  slidesPerView: 2,
  spaceBetween: 16,
  breakpoints: {
    480: { slidesPerView: 3, spaceBetween: 16 },
    640: { slidesPerView: 4, spaceBetween: 16 },
    1024: { slidesPerView: 5, spaceBetween: 16 },
    1280: { slidesPerView: 7, spaceBetween: 16 },
  },
})

onMounted(() => {
  if (props.items.length) {
    swiper.instance.value?.autoplay?.start()
  }
})
</script>

<template>
  <ClientOnly>
    <swiper-container
      v-if="items.length"
      ref="carouselRef"
      :init="false"
      class="logo-carousel"
      :class="{ compact }"
    >
      <swiper-slide
        v-for="item in items"
        :key="(item.code ?? 'extra') + '-' + (item.src ?? 'text')"
        class="swiper-slide"
        :class="{ clickable: !!(item.slug || item.code) }"
        @click="onLogoClick(item)"
      >
        <img v-if="item.src" :src="item.src" :alt="item.alt + ' logo'" width="140" height="64" loading="lazy">
        <span v-else class="logo-text">{{ item.alt }}</span>
      </swiper-slide>
    </swiper-container>
  </ClientOnly>
</template>

<style scoped>
.logo-carousel {
  margin-top: 2rem;
  flex-grow: 1;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  &.compact {
    margin-top: 1.5rem;
  }
}

.swiper-slide {
  background-color: var(--white);
  font-size: .88rem;
  border: 2px solid var(--ligth-blue);
  color: var(--blue);
  padding: .5rem 1.7rem;
  border-radius: 1.25rem;
  display: flex;
  gap: .7rem;
  align-items: center;
  cursor: default;
  justify-content: center;
  box-sizing: border-box;
  min-width: 0;

  &.clickable {
    cursor: pointer;
  }
}

img {
  height: 4rem;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  display: block;
}

.logo-text {
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: .88rem;
  font-weight: 600;
  line-height: 1.2;
  color: var(--blue);
}
</style>
